import type { Article } from './articles';

export const articlesStep05Ext: Article[] = [
  {
    id: 44,
    step: '05',
    title: "契約後にやることリスト｜住宅ローン本審査・色決め・仕様決め・外構計画の完全ガイド",
    category: "契約・引き渡し",
    date: "2026年3月28日",
    readTime: "約16分",
    image: "https://public.readdy.ai/ai/img_res/edited_acd4aceb2b1e380a82f361386e6e8e73_b429da52.jpg",
    excerpt: "「契約後に何をすればいいのか分からない」鳥取市・米子市・倉吉市で家づくりを進める方へ。住宅ローン本審査・色決め・仕様決め・外構計画・着工前確認まで、契約後にやるべき5ステップを順番にまとめました。",
    tags: ["契約後", "仕様決め", "住宅ローン本審査"],
    content: `
      <h2>はじめに</h2>
      <p>契約が終わると「やっと一安心…」と思いたくなりますが、実はここからが家づくりの本番です。鳥取市・米子市・倉吉市で家づくりを進める20〜30代の方からは、「契約後に何をすればいいのか分からない」「打ち合わせが多すぎて混乱する」という声をよく聞きます。私自身も契約後の流れを理解していなかったため、打ち合わせのたびに慌てて準備した経験があります。</p>
      <p>契約後のステップを理解しておくと、家づくりがスムーズに進み、<strong>追加費用や後悔を防ぐことができます</strong>。この記事では、初心者でも迷わないように、契約後に必ずやるべきことを順番にまとめました。</p>

      <h2>結論</h2>
      <p>契約後にやるべきことは、大きく分けて次の5つです。</p>
      <ol>
        <li>住宅ローン本審査</li>
        <li>色決め・仕様決め（内装・外装・設備）</li>
        <li>詳細図面の確定（電気図・収納計画）</li>
        <li>外構計画の決定</li>
        <li>着工前の最終確認（着工合意）</li>
      </ol>
      <p>この5つを順番に進めることで、家づくりのトラブルを防ぎ、<strong>スムーズに着工へ進む</strong>ことができます。</p>

      <h2>契約後にやること①：住宅ローン本審査</h2>
      <p>契約後すぐに行うのが<strong>住宅ローンの本審査</strong>です。</p>
      <p>本審査で提出するもの</p>
      <ul>
        <li>源泉徴収票</li>
        <li>住民票</li>
        <li>印鑑証明</li>
        <li>健康保険証</li>
        <li>住宅会社の見積書・図面</li>
      </ul>
      <p>本審査で見られるポイント</p>
      <ul>
        <li>返済比率（年収に対する返済額）</li>
        <li>信用情報（クレジット履歴）</li>
        <li>勤続年数</li>
        <li>他の借入状況（車のローンなど）</li>
      </ul>
      <p>鳥取県では地銀（鳥取銀行・山陰合同銀行）とネット銀行の併用比較が多く、<strong>3社以上の比較</strong>が一般的です。</p>

      <h2>契約後にやること②：色決め・仕様決め（内装・外装・設備）</h2>
      <p>契約後の打ち合わせで最も時間がかかるのが<strong>色決め・仕様決め</strong>です。</p>
      <p>決める内容</p>
      <ul>
        <li>外壁の色・素材</li>
        <li>屋根の色</li>
        <li>床材（フローリング）</li>
        <li>壁紙（クロス）</li>
        <li>キッチン・お風呂・洗面台の仕様</li>
        <li>建具（ドア）の色</li>
        <li>コンセント・スイッチの位置</li>
      </ul>
      <p>後悔しやすいポイント</p>
      <ul>
        <li>クロスの色が多すぎて統一感がなくなる</li>
        <li>コンセントの位置が足りない</li>
        <li>キッチンの高さが合わない</li>
        <li>収納の中の棚を決め忘れる</li>
      </ul>
      <p>鳥取の冬は寒いため、<strong>断熱性能や窓の仕様（樹脂サッシ・トリプルガラス）</strong>は特に慎重に選ぶべきポイントです。</p>

      <h2>契約後にやること③：詳細図面の確定（電気図・収納計画）</h2>
      <p>色決めと同時に進むのが<strong>詳細図面の確定</strong>です。</p>
      <p>確認する図面</p>
      <ul>
        <li>電気図（照明・スイッチ・コンセント）</li>
        <li>配置図</li>
        <li>収納内部の棚配置</li>
        <li>キッチン・洗面の高さ</li>
        <li>窓の位置・大きさ</li>
      </ul>
      <p>注意点</p>
      <ul>
        <li>電気図は生活動線をイメージして決める</li>
        <li>収納は「何を入れるか」まで考える</li>
        <li>窓の位置は家具配置とセットで考える</li>
      </ul>
      <p>特に電気図は後から変更しにくいため、<strong>最重要ポイント</strong>です。</p>

      <h2>契約後にやること④：外構計画の決定</h2>
      <p>外構は後回しにされがちですが、<strong>生活のしやすさに直結</strong>します。</p>
      <p>外構で決める内容</p>
      <ul>
        <li>駐車場のコンクリート</li>
        <li>アプローチ</li>
        <li>フェンス</li>
        <li>砂利敷き</li>
        <li>植栽</li>
        <li>ウッドデッキ</li>
      </ul>
      <p>鳥取での注意点</p>
      <ul>
        <li>冬の雪で滑りにくい素材を選ぶ</li>
        <li>風が強い地域はフェンスの強度が重要</li>
        <li>雨が多いので水はけを意識する</li>
      </ul>
      <p>外構は予算オーバーしやすいため、<strong>最初から予算に組み込む</strong>ことが大切です。</p>

      <h2>契約後にやること⑤：着工前の最終確認（着工合意）</h2>
      <p>すべての仕様・図面が確定したら、<strong>着工前の最終確認（着工合意）</strong>を行います。</p>
      <p>着工合意で確認すること</p>
      <ul>
        <li>最終見積もり</li>
        <li>最終図面</li>
        <li>工期（着工〜引き渡し）</li>
        <li>追加費用の有無</li>
        <li>ローンの実行時期</li>
      </ul>
      <p>ここでサインすると、いよいよ工事がスタートします。</p>

      <h2>まとめ</h2>
      <p>契約後にやることは次の5つです。</p>
      <ul>
        <li>住宅ローン本審査</li>
        <li>色決め・仕様決め</li>
        <li>詳細図面の確定</li>
        <li>外構計画</li>
        <li>着工前の最終確認</li>
      </ul>
      <p>この流れを理解しておくことで、<strong>家づくりがスムーズに進み、後悔や追加費用を防ぐ</strong>ことができます。</p>
      <p>鳥取県は冬季に工事が遅れやすいため、スケジュールは余裕を持って考えるのがポイントです。</p>

      <h2>次に読む記事</h2>
      <p>契約後の流れが分かったら、次に気になるのは「引き渡し前に何をチェックすればいいのか？」という点だと思います。引き渡し前のチェックは、住んでからの後悔を防ぐ最重要ポイントです。</p>
      <p>👉 次の記事：<a href="/articles/45">【引き渡し前チェックリスト】絶対に見落とせない20項目｜傷・設備・不具合の確認ポイント</a></p>
    `
  },
  {
    id: 45,
    step: '05',
    title: "引き渡し前チェックリスト｜絶対に見落とせない20項目｜傷・設備・不具合の確認ポイント",
    category: "契約・引き渡し",
    date: "2026年3月28日",
    readTime: "約18分",
    image: "https://public.readdy.ai/ai/img_res/edited_855076ca2b6086caae04c59a9dd2998a_22c9b2d5.jpg",
    excerpt: "「どこを見ればいいの？」「素人でもチェックできるの？」鳥取市・米子市・倉吉市で家づくりをする方へ。引き渡し前に絶対チェックすべき20項目を、傷・設備・動作・水回り・外回りのカテゴリ別にわかりやすく解説します。",
    tags: ["引き渡し前", "チェックリスト", "20項目"],
    content: `
      <h2>はじめに</h2>
      <p>引き渡し前のチェックは、家づくりの中でも最も緊張する場面のひとつです。「どこを見ればいいの？」「素人でもチェックできるの？」「見落として後悔したくない…」という声は、鳥取市・米子市・倉吉市の20〜30代の方から本当によく聞きます。</p>
      <p>私自身も初めての家づくりでは、引き渡し前のチェックで何を確認すべきか分からず、後から「ここも見ておけばよかった…」と後悔した経験があります。</p>
      <p>この記事では、初心者でも迷わず確認できるように<strong>引き渡し前に絶対チェックすべき20項目</strong>をわかりやすくまとめました。</p>

      <h2>結論</h2>
      <p>引き渡し前チェックで最も重要なのは、<strong>「傷・設備・動作・水回り・外回り」の5つを徹底的に確認すること</strong>。特に以下の20項目は、後からの修理が難しく、必ずチェックすべきポイントです。</p>

      <h2>① 室内の傷・汚れチェック（最重要）</h2>
      <p>チェックポイント</p>
      <ul>
        <li>床の傷・凹み</li>
        <li>壁紙のめくれ・浮き</li>
        <li>建具（ドア）の擦れ</li>
        <li>巾木の傷</li>
        <li>階段の傷</li>
        <li>窓枠の傷</li>
      </ul>
      <p>よくある失敗</p>
      <ul>
        <li>家具搬入後に傷を見つけても、施工時か判断できず対応してもらえない</li>
        <li>壁紙の浮きは後から広がることがある</li>
      </ul>

      <h2>② 建具（ドア・引き戸）の動作確認</h2>
      <ul>
        <li>開閉がスムーズか</li>
        <li>引き戸がレールから外れないか</li>
        <li>ドアの閉まりが強すぎないか</li>
        <li>建具の立て付けが悪くないか</li>
      </ul>
      <p>鳥取の冬は乾燥しやすく、<strong>建具の反りが起きやすいので要注意</strong>です。</p>

      <h2>③ 窓・サッシの動作確認</h2>
      <ul>
        <li>開閉が重くないか</li>
        <li>ロックが正常にかかるか</li>
        <li>網戸の動き</li>
        <li>ガラスに傷がないか</li>
        <li>結露対策の仕様が説明通りか</li>
      </ul>

      <h2>④ 水回り（キッチン・洗面・お風呂・トイレ）の動作確認</h2>
      <ul>
        <li>水が正常に流れるか</li>
        <li>排水の匂いがしないか</li>
        <li>水漏れがないか</li>
        <li>シャワーの水圧</li>
        <li>トイレの流れ</li>
      </ul>

      <h2>⑤ 電気・照明・スイッチの確認</h2>
      <ul>
        <li>全ての照明が点灯するか</li>
        <li>スイッチの位置が図面通りか</li>
        <li>コンセントの数・位置</li>
        <li>TV端子・LAN端子の位置</li>
        <li>インターホンの動作</li>
      </ul>

      <h2>⑥ 換気設備・エアコンの確認</h2>
      <ul>
        <li>24時間換気が正常に動いているか</li>
        <li>フィルターの取り外し方法</li>
        <li>エアコンの動作（オプションの場合）</li>
      </ul>
      <p>鳥取は湿気が多いため、<strong>換気設備は特に重要</strong>です。</p>

      <h2>⑦ キッチン設備の確認</h2>
      <ul>
        <li>IH・ガスコンロの動作</li>
        <li>食洗機の動作</li>
        <li>換気扇の吸い込み</li>
        <li>キッチン収納の開閉</li>
        <li>ワークトップの傷</li>
      </ul>

      <h2>⑧ お風呂・洗面の確認</h2>
      <ul>
        <li>浴槽の傷</li>
        <li>排水の流れ</li>
        <li>換気扇の動作</li>
        <li>洗面台の収納</li>
        <li>鏡の傷</li>
      </ul>

      <h2>⑨ トイレの確認</h2>
      <ul>
        <li>水の流れ</li>
        <li>ウォシュレットの動作</li>
        <li>収納の使い勝手</li>
        <li>匂いがこもっていないか</li>
      </ul>

      <h2>⑩ 収納内部の確認</h2>
      <ul>
        <li>棚板のぐらつき</li>
        <li>扉の開閉</li>
        <li>クロスの仕上げ</li>
        <li>ハンガーパイプの強度</li>
      </ul>

      <h2>⑪ 外壁・屋根の確認</h2>
      <ul>
        <li>外壁の傷・汚れ</li>
        <li>コーキングの仕上げ</li>
        <li>屋根のズレ（目視でOK）</li>
      </ul>

      <h2>⑫ ベランダ・バルコニーの確認</h2>
      <ul>
        <li>排水口の詰まり</li>
        <li>防水の仕上げ</li>
        <li>手すりの強度</li>
      </ul>
      <p>鳥取は雨が多いため、<strong>排水は特に重要</strong>です。</p>

      <h2>⑬ 外構の確認</h2>
      <ul>
        <li>駐車場コンクリートのひび割れ</li>
        <li>フェンスのぐらつき</li>
        <li>砂利の量</li>
        <li>アプローチの仕上げ</li>
      </ul>

      <h2>⑭ 設備保証・取扱説明書の確認</h2>
      <ul>
        <li>取扱説明書が揃っているか</li>
        <li>保証書の有無</li>
        <li>メーカー保証の期間</li>
      </ul>

      <h2>⑮ 引き渡し後のアフターサービス説明</h2>
      <ul>
        <li>定期点検の時期</li>
        <li>緊急時の連絡先</li>
        <li>無償対応の範囲</li>
      </ul>

      <h2>まとめ</h2>
      <p>引き渡し前に絶対チェックすべき項目は以下の通りです。</p>
      <ul>
        <li>室内の傷・建具の動作・窓・サッシ</li>
        <li>水回り・電気・照明・換気設備</li>
        <li>キッチン・お風呂・トイレ・収納</li>
        <li>外壁・ベランダ・外構</li>
        <li>保証書・アフター説明</li>
      </ul>
      <p>これらを確認しておくことで、<strong>住んでからの後悔を大幅に減らせます</strong>。</p>

      <h2>次に読む記事</h2>
      <p>引き渡し前チェックが終わったら、次に気になるのは「引き渡しまでのスケジュール」だと思います。鍵渡し・登記・火災保険・引っ越し準備など、やることが多い時期だからこそ、流れを知っておくと安心です。</p>
      <p>👉 次の記事：<a href="/articles/46">鳥取で家の引き渡しまでのスケジュール｜鍵渡し・登記・火災保険の準備まとめ</a></p>
    `
  },
  {
    id: 46,
    step: '05',
    title: "鳥取で家の引き渡しまでのスケジュール｜鍵渡し・登記・火災保険の準備まとめ",
    category: "契約・引き渡し",
    date: "2026年3月28日",
    readTime: "約16分",
    image: "https://public.readdy.ai/ai/img_res/edited_ba2230472e8f01718972f7c0f95fbac6_5309dbbb.jpg",
    excerpt: "「引き渡しまでに何をすればいいの？」「鍵渡しってどんな流れ？」鳥取市・米子市・倉吉市の20〜30代の方へ。引き渡し1〜2ヶ月前から当日まで、登記・火災保険・鍵渡し・残金決済の流れを完全ガイドとしてまとめました。",
    tags: ["引き渡し", "鍵渡し", "スケジュール"],
    content: `
      <h2>はじめに</h2>
      <p>家づくりの終盤に近づくと、「引き渡しまでに何をすればいいの？」「鍵渡しってどんな流れ？」「登記や火災保険の準備はいつやるの？」と不安が一気に増えてきます。鳥取市・米子市・倉吉市の20〜30代の方からも、引き渡し前後のスケジュールが分からず焦ってしまうという声をよく聞きます。</p>
      <p>私自身も初めての家づくりでは、引き渡し直前にやることが多すぎて混乱した経験があります。ですが、流れを理解しておくだけで、引き渡しまでをスムーズに進められ、余計なトラブルを防ぐことができます。</p>
      <p>この記事では、初心者でも迷わないように<strong>引き渡し1〜2ヶ月前から当日までのスケジュールを完全ガイド</strong>としてまとめました。</p>

      <h2>結論（先に答えを提示）</h2>
      <p>引き渡しまでの流れは、次の5ステップで進みます。</p>
      <ol>
        <li>引き渡し1〜2ヶ月前：登記・火災保険の準備開始</li>
        <li>引き渡し1ヶ月前：ローン実行日の確定・外構の最終調整</li>
        <li>引き渡し2週間前：完了検査・引き渡し前チェック</li>
        <li>引き渡し当日：鍵渡し・ローン実行・残金決済</li>
        <li>引き渡し後：住所変更・保証書の管理・引っ越し</li>
      </ol>
      <p>この流れを理解しておくと、引き渡し直前の慌ただしさが大幅に減り、安心して新生活を迎えられます。</p>

      <h2>引き渡し1〜2ヶ月前にやること</h2>

      <h3>火災保険の見積もり・加入準備</h3>
      <p>住宅ローンを利用する場合、<strong>火災保険は加入必須</strong>です。</p>
      <p>選ぶポイント</p>
      <ul>
        <li>補償内容（火災・風災・水災・破損）</li>
        <li>地震保険の有無</li>
        <li>保険期間（10年一括が最安）</li>
      </ul>
      <p>鳥取県は雪災・風災・地震リスクがあるため、<strong>補償内容は慎重に選ぶ</strong>必要があります。</p>

      <h3>表題登記の準備</h3>
      <p>建物が完成すると、<strong>表題登記（建物の登録）</strong>を行います。通常は住宅会社または司法書士が代行しますが、費用の負担者は契約内容によって異なるため確認が必要です。</p>

      <h3>ローン実行日の調整</h3>
      <p>引き渡し日に合わせて、<strong>住宅ローンの実行日（融資実行日）</strong>を銀行と調整します。ローン実行日＝残金支払い日となるため、引き渡し日とセットで決める必要があります。</p>

      <h2>引き渡し1ヶ月前にやること</h2>

      <h3>外構工事の最終確認</h3>
      <p>確認ポイント</p>
      <ul>
        <li>駐車場コンクリートの範囲</li>
        <li>フェンスの位置</li>
        <li>砂利・芝生の量</li>
        <li>アプローチの仕上げ</li>
      </ul>
      <p>鳥取は雨が多いため、<strong>水はけの確認</strong>も重要です。</p>

      <h3>引っ越し業者の予約</h3>
      <p>引き渡し後すぐに入居する場合は、このタイミングで引っ越し業者を予約します。<strong>3月は繁忙期で予約が取りにくいため、早めの予約が必須</strong>です。</p>

      <h2>引き渡し2週間前にやること</h2>

      <h3>完了検査（役所・住宅会社）</h3>
      <p>建物が完成すると、<strong>完了検査（行政検査）</strong>が行われます。その後、住宅会社と一緒に社内検査 → お客様検査（施主検査）という流れで進みます。</p>

      <h3>引き渡し前チェック（施主検査）</h3>
      <p>施主検査では、以下を徹底的に確認します。</p>
      <ul>
        <li>傷・汚れ</li>
        <li>建具の動作</li>
        <li>水回りの動作</li>
        <li>電気・照明</li>
        <li>換気設備</li>
        <li>外構の仕上げ</li>
      </ul>
      <p>ここで見つかった不具合は、<strong>引き渡しまでに修正</strong>されます。</p>

      <h2>引き渡し当日にやること</h2>

      <h3>鍵の受け取り（鍵渡し）</h3>
      <p>引き渡し当日は、住宅会社の担当者と<strong>鍵の受け渡し</strong>を行います。</p>
      <p>受け取るもの</p>
      <ul>
        <li>玄関鍵（複数本）</li>
        <li>カードキー・スマートキー</li>
        <li>各設備の取扱説明書</li>
        <li>保証書一式</li>
      </ul>

      <h3>残金決済（ローン実行）</h3>
      <p>銀行で</p>
      <ul>
        <li>残金の支払い</li>
        <li>住宅ローンの実行</li>
        <li>抵当権設定登記</li>
      </ul>
      <p>を行います。司法書士が同席することが多いです。</p>

      <h3>最終書類の受け取り</h3>
      <ul>
        <li>保証書</li>
        <li>取扱説明書</li>
        <li>引き渡し確認書</li>
        <li>登記関連書類（後日郵送の場合あり）</li>
      </ul>

      <h2>引き渡し後にやること</h2>

      <h3>住所変更・各種手続き</h3>
      <ul>
        <li>住民票の移動</li>
        <li>銀行・保険の住所変更</li>
        <li>子どもの学校・保育園の手続き</li>
        <li>運転免許証の住所変更</li>
      </ul>

      <h3>電気・ガス・水道の開始手続き</h3>
      <p>引っ越し前に電気・ガス・水道の開始手続きを行います。<strong>ガスは立ち会いが必要</strong>な場合があります。</p>

      <h3>引っ越し・家具搬入</h3>
      <p>引き渡し後、いよいよ新生活のスタートです。家具搬入時に傷がつかないよう、<strong>養生をしっかり行う</strong>ことが大切です。</p>

      <h2>まとめ</h2>
      <p>引き渡しまでのスケジュールは次の通りです。</p>
      <ul>
        <li>1〜2ヶ月前：火災保険・登記準備</li>
        <li>1ヶ月前：ローン実行日調整・外構最終確認</li>
        <li>2週間前：完了検査・施主検査</li>
        <li>当日：鍵渡し・残金決済・書類受け取り</li>
        <li>引き渡し後：住所変更・引っ越し・ライフライン開始</li>
      </ul>
      <p>この流れを理解しておくことで、引き渡し前後の不安が大幅に減り、<strong>スムーズに新生活を迎えられます</strong>。</p>

      <h2>次に読む記事</h2>
      <p>引き渡しまでの流れが分かると、「最初からの流れをもう一度整理したい」と感じる方がとても多いです。家づくりは情報収集から契約・引き渡しまで長いプロセスだからこそ、全体像をつかんでおくと迷いがなくなります。</p>
      <p>👉 次の記事：<a href="/articles/47">家づくり全体ロードマップ｜5ステップで全体像がわかる完全ガイド（鳥取版）</a></p>
    `
  },
  {
    id: 47,
    step: '05',
    title: "家づくり全体ロードマップ｜5ステップで全体像がわかる完全ガイド（鳥取版）",
    category: "契約・引き渡し",
    date: "2026年3月28日",
    readTime: "約14分",
    image: "https://public.readdy.ai/ai/img_res/edited_4a172d60305593f035058f8180568b23_34ed856c.jpg",
    excerpt: "「家づくりの流れを最初から整理したい」という方へ。鳥取版の家づくり全体ロードマップを5ステップで完全解説。知識をつける→予算→パートナー選び→プラン決め→契約・引き渡しまで、全体像が一気にわかります。",
    tags: ["ロードマップ", "5ステップ", "全体像"],
    content: `
      <h2>はじめに</h2>
      <p>「家づくりって、全体的にどんな流れで進むんだろう？」<br />「情報収集から引き渡しまで、大まかなステップを知りたい」<br />「ここまで来たけど、全体像をもう一度整理したい」</p>
      <p>家づくりは長いプロセスだからこそ、<strong>全体像をつかんでおくと迷いがなくなり、次に何をすべきかが明確になります</strong>。この記事では、鳥取市・米子市・倉吉市で家づくりを進める方のために、家づくりの流れを5ステップでわかりやすくまとめました。</p>

      <h2>結論：家づくりは5ステップで進む</h2>
      <ol>
        <li>知識をつける（全体像を理解する）</li>
        <li>予算を決める（無理のない資金計画）</li>
        <li>パートナーを決める（住宅会社選び）</li>
        <li>物件・プランを決める（土地＋間取り）</li>
        <li>契約・引き渡し（最終ステップ）</li>
      </ol>
      <p>この5ステップを理解しておけば、家づくりの迷いがなくなり、安心して進められます。</p>

      <h2>ステップ① 知識をつける（全体像を理解する）</h2>
      <p>家づくりの最初のステップは、<strong>全体像を理解すること</strong>。ここを飛ばすと、住宅会社の言われるままに進んでしまい、後悔につながります。</p>

      <h3>学ぶべきポイント</h3>
      <ul>
        <li>家づくりの流れ（情報収集→契約→引き渡し）</li>
        <li>住宅会社の種類（工務店・HM・ローコスト）</li>
        <li>間取りの基本（動線・収納・日当たり）</li>
        <li>住宅ローンの基礎（変動・固定・フラット35）</li>
      </ul>

      <h3>行動</h3>
      <ul>
        <li>SNS・YouTubeで実例を見る</li>
        <li>完成見学会に行く</li>
        <li>家づくりの本を1冊読む</li>
      </ul>
      <p>鳥取県は地域差が大きいため、<strong>地元の見学会に行くことが最も学びが深い</strong>です。</p>

      <h2>ステップ② 予算を決める（無理のない資金計画）</h2>
      <p>家づくりの失敗の多くは、<strong>予算の決め方を間違えることから始まります</strong>。</p>

      <h3>決めるポイント</h3>
      <ul>
        <li>月々の返済額（無理のないライン）</li>
        <li>頭金の有無</li>
        <li>車のローンなど既存の借入</li>
        <li>建物＋土地＋諸費用の総額</li>
      </ul>

      <h3>鳥取の相場感</h3>
      <table>
        <thead>
          <tr><th>項目</th><th>目安金額</th></tr>
        </thead>
        <tbody>
          <tr><td>建物</td><td>1,800〜2,800万円</td></tr>
          <tr><td>土地</td><td>500〜900万円</td></tr>
          <tr><td>諸費用</td><td>150〜250万円</td></tr>
        </tbody>
      </table>
      <p>総額<strong>2,500〜3,500万円がボリュームゾーン</strong>です。</p>

      <h2>ステップ③ パートナーを決める（住宅会社選び）</h2>
      <p>家づくりの満足度の<strong>8割は住宅会社選びで決まる</strong>と言われます。</p>

      <h3>比較ポイント</h3>
      <ul>
        <li>性能（断熱・気密）</li>
        <li>標準仕様</li>
        <li>価格帯</li>
        <li>担当者との相性</li>
        <li>アフターサービス</li>
      </ul>

      <h3>鳥取でよく比較されるタイプ</h3>
      <ul>
        <li>地元工務店（性能が高い）</li>
        <li>ローコスト住宅（価格が安い）</li>
        <li>大手ハウスメーカー（保証が手厚い）</li>
      </ul>
      <p>3社以上比較すると、違いがよく分かります。</p>

      <h2>ステップ④ 物件・プランを決める（土地＋間取り）</h2>
      <p>住宅会社が決まったら、次は<strong>土地と間取り</strong>を決めます。</p>

      <h3>土地のチェックポイント</h3>
      <ul>
        <li>日当たり</li>
        <li>学校区</li>
        <li>ハザードマップ</li>
        <li>道路の広さ</li>
        <li>上下水道の状況</li>
      </ul>
      <p>鳥取市・米子市は地盤の強さがエリアで大きく違うため、<strong>地盤調査の結果は必ず確認</strong>。</p>

      <h3>間取りのポイント</h3>
      <ul>
        <li>家事動線（回遊動線が人気）</li>
        <li>収納の量と位置</li>
        <li>南向きリビング</li>
        <li>1階に1部屋</li>
        <li>室内干しスペース</li>
      </ul>
      <p>間取りは<strong>生活の快適さを左右するため、最も時間をかけるべき工程</strong>です。</p>

      <h2>ステップ⑤ 契約・引き渡し（最終ステップ）</h2>
      <p>間取り・見積もりが固まったら契約し、いよいよ家づくりが本格スタートします。</p>

      <h3>契約前に確認すべきこと</h3>
      <ul>
        <li>見積もりの範囲</li>
        <li>外構・地盤改良の扱い</li>
        <li>標準仕様</li>
        <li>追加費用の条件</li>
        <li>工期</li>
        <li>解約時の違約金</li>
      </ul>

      <h3>契約後の流れ</h3>
      <ul>
        <li>住宅ローン本審査</li>
        <li>色決め・仕様決め</li>
        <li>電気図の確定</li>
        <li>外構計画</li>
        <li>着工</li>
        <li>完了検査</li>
        <li>引き渡し（鍵渡し）</li>
      </ul>
      <p>ここまで来れば、あとは<strong>新生活を迎えるだけ</strong>です。</p>

      <h2>まとめ：5ステップで家づくりは迷わなくなる</h2>
      <ul>
        <li>知識をつける（全体像を理解）</li>
        <li>予算を決める（無理のない資金計画）</li>
        <li>パートナーを決める（住宅会社選び）</li>
        <li>物件・プランを決める（土地＋間取り）</li>
        <li>契約・引き渡し（最終ステップ）</li>
      </ul>
      <p>この5ステップを理解しておけば、家づくりの迷いがなくなり、<strong>安心して進められます</strong>。鳥取で家づくりを検討している方は、まず全体像を理解した上で、一歩ずつ進んでいきましょう。</p>

      <h2>次に読む記事</h2>
      <p>家づくりの全体像が分かったら、まずは知識をつけるところから始めましょう。</p>
      <p>👉 <a href="/articles/7">鳥取で家づくりは何から始める？初心者が最初にやるべき5ステップ</a>を読むと、最初の一歩がより明確になります。</p>
    `
  }
  ,
  {
    id: 48,
    step: '04',
    title: "【鳥取版】失敗しない土地選びのコツ10選｜日当たり・地盤・ハザードのチェックポイント",
    category: "物件・プランを決める",
    date: "2026年3月28日",
    readTime: "約18分",
    image: "https://public.readdy.ai/ai/img_res/edited_0be8b0c7e8fb414c430d867207ad4a9d_3b6d2b06.jpg",
    excerpt: "「土地ってどう選べばいいの？」「日当たり？地盤？ハザード？何を優先すればいいの？」鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方へ。初心者でも迷わず選べる10のポイントをわかりやすく解説します。",
    tags: ["鳥取", "土地選び", "10選"],
    content: `
      <h2>はじめに</h2>
      <p>「土地ってどう選べばいいの？」「日当たり？地盤？ハザード？何を優先すればいいの？」<br />鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方から、こうした悩みを本当に多く聞きます。</p>
      <p>私自身も最初は「土地は不動産サイトで探せばいい」と思っていましたが、実際に探し始めると、<strong>"良い土地"と"買ってはいけない土地"の差が大きい</strong>ことに気づきました。</p>
      <p>この記事では、初心者でも迷わず選べるように、<strong>鳥取の土地選びで絶対に押さえるべき10のポイント</strong>をまとめました。</p>

      <h2>結論</h2>
      <p>土地選びで最も重要なのは、<strong>「日当たり × 地盤 × ハザード × 生活動線 × 予算」</strong>この5つのバランスです。</p>
      <p>特に鳥取県は、</p>
      <ul>
        <li>地盤の強さがエリアで大きく違う</li>
        <li>冬の日当たりが生活の快適さに直結</li>
        <li>水害リスクが地域差あり</li>
      </ul>
      <p>という特徴があるため、全国共通の土地選びよりも<strong>"地域性"</strong>が重要になります。</p>

      <h2>① 日当たり（南向き・東向きが人気）</h2>
      <p>鳥取の冬は日照時間が短く、日当たりの良さが快適さに直結します。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>南向きのリビングが確保できるか</li>
        <li>前面道路の幅（狭いと日が入りにくい）</li>
        <li>隣家との距離</li>
      </ul>
      <p><strong>注意点</strong></p>
      <ul>
        <li>北向きでも間取り次第で明るくできる</li>
        <li>旗竿地は日当たりが弱いことが多い</li>
      </ul>

      <h2>② 地盤の強さ（鳥取はエリア差が大きい）</h2>
      <p>鳥取市・米子市は、地盤が弱いエリアと強いエリアがはっきり分かれています。</p>
      <p><strong>地盤が弱いと…</strong></p>
      <ul>
        <li>地盤改良費が50〜150万円かかる</li>
        <li>将来の不同沈下リスクがある</li>
      </ul>
      <p><strong>チェック方法</strong></p>
      <ul>
        <li>地盤サポートマップ</li>
        <li>近隣の地盤改良の有無</li>
        <li>不動産会社・住宅会社に確認</li>
      </ul>

      <h2>③ ハザードマップ（水害・地震）</h2>
      <p>鳥取県は地震・水害のリスクが地域によって異なります。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>洪水・浸水リスク</li>
        <li>土砂災害警戒区域</li>
        <li>津波リスク（米子市沿岸部）</li>
      </ul>
      <p><strong>注意点</strong></p>
      <ul>
        <li>リスクが高い土地は価格が安い</li>
        <li>保険料が高くなる場合がある</li>
      </ul>

      <h2>④ 道路の幅・接道条件</h2>
      <p>家は道路がないと建てられません。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>道路幅4m以上が理想</li>
        <li>車の出し入れがしやすいか</li>
        <li>雪の日に車が動かせるか（鳥取は重要）</li>
      </ul>

      <h2>⑤ 上下水道・ガスの状況</h2>
      <p>インフラ状況は土地価格に大きく影響します。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>上水道の引き込み状況</li>
        <li>下水道 or 浄化槽</li>
        <li>都市ガス or プロパン</li>
      </ul>
      <p><strong>注意点</strong></p>
      <ul>
        <li>上下水道の引き込みがないと追加費用が高い</li>
        <li>プロパンは都市ガスよりランニングコストが高い</li>
      </ul>

      <h2>⑥ 形状（整形地が理想）</h2>
      <p>土地の形は間取りの自由度に影響します。</p>
      <p><strong>良い土地</strong></p>
      <ul>
        <li>正方形・長方形の整形地</li>
        <li>間口が広い土地</li>
      </ul>
      <p><strong>注意が必要な土地</strong></p>
      <ul>
        <li>旗竿地</li>
        <li>三角地</li>
        <li>細長い土地</li>
      </ul>

      <h2>⑦ 周辺環境（生活動線）</h2>
      <p>土地は"暮らしやすさ"で選ぶことが大切です。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>スーパー・ドラッグストア</li>
        <li>小学校・保育園</li>
        <li>病院</li>
        <li>通勤ルート</li>
        <li>騒音（交通量・線路・工場）</li>
      </ul>
      <p>鳥取は車移動が中心なので、<strong>生活動線の良さが満足度に直結</strong>します。</p>

      <h2>⑧ 価格（総額で考える）</h2>
      <p>土地は"土地代＋造成費＋外構費＋地盤改良費"で総額が決まります。</p>
      <p><strong>よくある落とし穴</strong></p>
      <ul>
        <li>土地代が安い → 造成費が高い</li>
        <li>土地代が安い → 地盤が弱い</li>
        <li>土地代が安い → 外構費が高い</li>
      </ul>
      <p>土地は<strong>"安い＝お得"ではありません</strong>。</p>

      <h2>⑨ 近隣の雰囲気・治安</h2>
      <p>実際に歩いてみると、ネットでは分からない情報が得られます。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>夜の雰囲気</li>
        <li>近隣住民の生活音</li>
        <li>ゴミ置き場の位置</li>
        <li>犬の鳴き声</li>
      </ul>

      <h2>⑩ 将来の資産価値</h2>
      <p>土地は資産として残るため、将来の価値も考えるべきです。</p>
      <p><strong>資産価値が落ちにくい土地</strong></p>
      <ul>
        <li>駅・学校が近い</li>
        <li>商業施設が充実</li>
        <li>ハザードリスクが低い</li>
        <li>整形地</li>
      </ul>

      <h2>まとめ</h2>
      <p>鳥取で土地選びに失敗しないためには、<strong>日当たり・地盤・ハザード・生活動線・価格</strong>この5つをバランスよく見ることが大切です。</p>
      <p>土地は一度買うと変更できないため、時間をかけて慎重に選ぶ価値があります。</p>

      <h2>次に読む記事</h2>
      <p>土地選びのポイントが分かったら、次に必要なのは「自分たちにとって理想の土地条件をどう決めるか？」という視点です。</p>
      <p>👉 次の記事：<a href="/articles/49">土地探しの優先順位はこれ！初心者が迷わない"理想の土地条件"の決め方（鳥取版）</a></p>
    `
  },
  {
    id: 49,
    step: '04',
    title: "土地探しの優先順位はこれ！初心者が迷わない理想の土地条件の決め方（鳥取版）",
    category: "物件・プランを決める",
    date: "2026年3月28日",
    readTime: "約16分",
    image: "https://public.readdy.ai/ai/img_res/edited_855076ca2b6086caae04c59a9dd2998a_6d498b5d.jpg",
    excerpt: "土地探しで何を優先すればいいのか分からない方へ。鳥取市・米子市・倉吉市で家づくりを始める方へ。生活動線・日当たり・学校区・ハザード・予算の5つを絶対条件・できれば・妥協できるの3段階で整理するだけで、土地探しが一気に楽になります。",
    tags: ["鳥取", "土地探し", "優先順位"],
    content: `
      <h2>はじめに</h2>
      <p>土地探しで最初にぶつかる壁は「何を優先すればいいのか分からない」という悩みです。鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方からも、「学校区も気になるし、日当たりも大事…でも予算もある…」という声をよく聞きます。</p>
      <p>土地は<strong>"完璧なものが存在しない"</strong>からこそ、優先順位を決めるだけで一気に迷いが減り、理想の土地が見つかりやすくなります。</p>

      <h2>土地条件を決める前に知っておきたい基本</h2>
      <p>土地選びは、複数の条件が絡み合う"総合判断"です。代表的な条件は次の通りです。</p>
      <ul>
        <li>日当たり</li>
        <li>地盤</li>
        <li>ハザード</li>
        <li>学校区</li>
        <li>通勤</li>
        <li>価格</li>
        <li>形状</li>
        <li>インフラ</li>
        <li>周辺環境</li>
      </ul>
      <p>これらをすべて満たす土地はほぼ存在しません。だからこそ<strong>「自分たちの暮らしにとって何が大切か」</strong>を整理することが最重要になります。</p>

      <h2>① 生活動線（通勤・学校・買い物）を最優先にする</h2>
      <p>土地選びで最初に考えるべきは<strong>生活動線</strong>です。鳥取は車移動が中心なので、生活のしやすさが満足度に直結します。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>通勤時間は何分まで許容できるか</li>
        <li>子どもの学校・保育園までの距離</li>
        <li>スーパー・ドラッグストアの近さ</li>
        <li>実家との距離（子育てサポート）</li>
      </ul>
      <p>生活動線が合わない土地は、どれだけ条件が良くても後悔につながります。</p>

      <h2>② 日当たりの優先度を決める（冬の鳥取は特に重要）</h2>
      <p>鳥取の冬は日照時間が短く、日当たりは快適さに直結します。</p>
      <p><strong>優先度の決め方</strong></p>
      <ul>
        <li>冬でも暖かい家にしたい → 南向き優先</li>
        <li>価格を抑えたい → 北向きでもOK</li>
        <li>プライバシー重視 → 東向き・西向きも選択肢</li>
      </ul>
      <p>日当たりは間取りで調整できる部分もあるため、<strong>"絶対条件"にするかどうかを最初に決める</strong>のがポイントです。</p>

      <h2>③ 学校区・治安・周辺環境の優先度を決める</h2>
      <p>子育て世帯にとって、学校区は大きな判断材料です。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>小学校・中学校までの距離</li>
        <li>通学路の安全性</li>
        <li>交通量の多さ</li>
        <li>夜の雰囲気</li>
        <li>近隣の生活音</li>
      </ul>
      <p>鳥取市・米子市はエリアによって雰囲気が大きく違うため、<strong>昼と夜の両方で現地を見る</strong>のが理想です。</p>

      <h2>④ ハザード・地盤の優先度を決める</h2>
      <p>鳥取県は地震・水害リスクが地域によって異なります。</p>
      <p><strong>ハザードの優先度</strong></p>
      <ul>
        <li>浸水リスクはどこまで許容するか</li>
        <li>土砂災害区域は避けるか</li>
        <li>津波リスク（米子市沿岸部）</li>
      </ul>
      <p><strong>地盤の優先度</strong></p>
      <ul>
        <li>地盤改良費を抑えたい → 地盤が強いエリア</li>
        <li>多少の改良費は許容 → 条件を広げる</li>
      </ul>
      <p>地盤は後から変えられないため、<strong>優先度を高めに設定する人が多い</strong>です。</p>

      <h2>⑤ 予算と総額のバランスを決める</h2>
      <p>土地は「土地代＋造成費＋外構費＋地盤改良費」で総額が決まります。</p>
      <p><strong>予算の優先順位</strong></p>
      <ul>
        <li>土地にお金をかける → 建物の仕様を調整</li>
        <li>建物にお金をかける → 土地はコンパクトに</li>
        <li>バランス重視 → 2,500〜3,500万円の総額帯が多い</li>
      </ul>
      <p>鳥取は土地が比較的安いため、<strong>建物に予算を回す人が多い</strong>のが特徴です。</p>

      <h2>優先順位の決め方（ワークシート形式）</h2>
      <p>次の5つを「絶対条件」「できれば」「妥協できる」の3段階で分類します。</p>
      <table>
        <thead>
          <tr><th>条件</th><th>絶対条件</th><th>できれば</th><th>妥協できる</th></tr>
        </thead>
        <tbody>
          <tr><td>生活動線（通勤・学校・買い物）</td><td>□</td><td>□</td><td>□</td></tr>
          <tr><td>日当たり</td><td>□</td><td>□</td><td>□</td></tr>
          <tr><td>学校区・周辺環境</td><td>□</td><td>□</td><td>□</td></tr>
          <tr><td>ハザード・地盤</td><td>□</td><td>□</td><td>□</td></tr>
          <tr><td>予算</td><td>□</td><td>□</td><td>□</td></tr>
        </tbody>
      </table>
      <p>分類すると、<strong>土地探しが一気に楽になります</strong>。</p>

      <h2>まとめ</h2>
      <p>土地選びは、以下の5つの優先順位を決めることで迷いがなくなります。</p>
      <ul>
        <li>生活動線</li>
        <li>日当たり</li>
        <li>学校区・周辺環境</li>
        <li>ハザード・地盤</li>
        <li>予算</li>
      </ul>
      <p>完璧な土地は存在しないため、<strong>自分たちの暮らしにとって何が大切か</strong>を明確にすることが最も重要です。</p>

      <h2>次に読む記事</h2>
      <p>土地条件の優先順位が整理できたら、次に気になるのは「その土地にどんな家を建てれば暮らしやすいのか？」という点です。土地と間取りはセットで考えることで、家づくりの失敗が大幅に減ります。</p>
      <p>👉 <a href="/articles/50">【間取りの基本】後悔しない家づくりの黄金ルール｜動線・収納・日当たりの考え方（鳥取版）</a></p>
    `
  },
  {
    id: 50,
    step: '04',
    title: "【間取りの基本】後悔しない家づくりの黄金ルール｜動線・収納・日当たりの考え方（鳥取版）",
    category: "物件・プランを決める",
    date: "2026年3月28日",
    readTime: "約18分",
    image: "https://public.readdy.ai/ai/img_res/edited_855076ca2b6086caae04c59a9dd2998a_2dbda08d.jpg",
    excerpt: "「間取りって何を考えればいいの？」家づくりで最も後悔が多い「間取り」について、鳥取市・米子市・倉吉市の20〜30代の方へ。動線・収納・日当たりの3つの軸で考えるだけで、暮らしの満足度が劇的に変わります。",
    tags: ["鳥取", "間取り", "黄金ルール"],
    content: `
      <h2>はじめに</h2>
      <p>家づくりで最も後悔が多いのが「間取り」です。鳥取市・米子市・倉吉市の20〜30代の方からも、「住んでみて気づいた不便さが多い」「もっと考えておけばよかった」という声をよく聞きます。間取りは"毎日の暮らしやすさ"を決める最重要ポイントであり、土地選びと同じくらい慎重に考える価値があります。</p>
      <p>ここでは、初心者でも迷わず間取りを考えられるように、後悔しないための黄金ルールを体系的にまとめました。</p>

      <h2>間取りで最初に考えるべき「3つの軸」</h2>
      <p>間取りは次の3つの軸で考えると、失敗が大幅に減ります。</p>
      <ul>
        <li>動線（家事・生活のしやすさ）</li>
        <li>収納（片付けやすさ）</li>
        <li>日当たり（快適さ）</li>
      </ul>
      <p>この3つが整うだけで、暮らしの満足度は劇的に上がります。</p>

      <h2>① 動線の黄金ルール（家事・生活がラクになる）</h2>
      <p>動線は「毎日のストレス量」を決める最重要ポイントです。</p>

      <h3>家事動線の基本</h3>
      <ul>
        <li>キッチン → パントリー → 玄関の動線</li>
        <li>洗濯 → 干す → しまう の距離を短く</li>
        <li>回遊動線（ぐるっと回れる動線）が人気</li>
      </ul>
      <p>鳥取は冬の天候が不安定なため、<strong>室内干しスペースは必須級</strong>です。</p>

      <h3>生活動線の基本</h3>
      <ul>
        <li>玄関からリビングまでの動線をスムーズに</li>
        <li>帰宅後すぐに手洗いできる位置に洗面台</li>
        <li>1階にファミリークローゼットがあると便利</li>
      </ul>

      <h2>② 収納の黄金ルール（片付けやすい家になる）</h2>
      <p>収納は「量」よりも「位置」が重要です。</p>

      <h3>収納の考え方</h3>
      <ul>
        <li>使う場所の近くに収納をつくる</li>
        <li>1階に大容量収納（ファミクロ・土間収納）</li>
        <li>キッチン周りはパントリーがあると便利</li>
      </ul>

      <h3>よくある後悔</h3>
      <ul>
        <li>収納は多いのに"使いにくい"</li>
        <li>2階にしか収納がなく、1階が散らかる</li>
        <li>玄関収納が足りず、ベビーカーやアウトドア用品が置けない</li>
      </ul>
      <p>収納は<strong>「生活動線とセット」</strong>で考えると成功します。</p>

      <h2>③ 日当たりの黄金ルール（冬の鳥取で特に重要）</h2>
      <p>鳥取の冬は日照時間が短いため、日当たりは快適さに直結します。</p>

      <h3>日当たりの考え方</h3>
      <ul>
        <li>南向きリビングが理想</li>
        <li>東向きは朝日が入り、気持ちよく起きられる</li>
        <li>西向きは夏に暑くなりやすい</li>
        <li>北向きは安定した光で書斎や家事室に向いている</li>
      </ul>

      <h3>注意点</h3>
      <ul>
        <li>日当たりは土地の形状・隣家の高さで大きく変わる</li>
        <li>間取りで調整できる部分が多い</li>
      </ul>

      <h2>間取りで後悔しやすいポイント（実例）</h2>
      <ul>
        <li>コンセントの位置が足りない</li>
        <li>玄関が狭くて混雑する</li>
        <li>キッチンが暗い</li>
        <li>トイレの位置がリビングに近すぎる</li>
        <li>階段の位置が悪く、2階が使いにくい</li>
      </ul>
      <p>これらは"間取りの基本"を押さえておけば防げます。</p>

      <h2>家族構成別の間取りの考え方</h2>

      <h3>子育て世帯</h3>
      <ul>
        <li>1階にファミリークローゼット</li>
        <li>室内干しスペース</li>
        <li>リビング階段</li>
        <li>玄関にベビーカー収納</li>
      </ul>

      <h3>共働き夫婦</h3>
      <ul>
        <li>家事動線を最短に</li>
        <li>パントリー必須</li>
        <li>ワークスペース</li>
        <li>回遊動線</li>
      </ul>

      <h3>将来を見据える場合</h3>
      <ul>
        <li>1階に1部屋</li>
        <li>バリアフリーを意識</li>
        <li>階段の位置を中央に</li>
      </ul>

      <h2>間取りを考えるときの"正しい順番"</h2>
      <ol>
        <li>家族の生活スタイルを整理</li>
        <li>必要な部屋数を決める</li>
        <li>動線を決める</li>
        <li>収納の位置を決める</li>
        <li>日当たりを考慮して配置</li>
        <li>最後に細かい設備・コンセントを決める</li>
      </ol>
      <p>この順番で考えると、間取りがブレません。</p>

      <h2>まとめ</h2>
      <p>間取りで後悔しないためには、次の3つを軸に考えることが重要です。</p>
      <ul>
        <li>動線（家事・生活のしやすさ）</li>
        <li>収納（片付けやすさ）</li>
        <li>日当たり（快適さ）</li>
      </ul>
      <p>この3つを押さえるだけで、暮らしやすさが大きく変わります。</p>

      <h2>次に読む記事</h2>
      <p>間取りの基本が理解できたら、次に気になるのは「自分たちの家族構成に合った間取りはどれか？」という点です。</p>
      <p>👉 <a href="/articles/51">4LDKは本当に必要？家族構成別の最適な間取りプラン｜平屋・2階建ての比較（鳥取版）</a>を読むと、家族構成別に"ちょうどいい間取り"が分かります。</p>
    `
  },
  {
    id: 51,
    step: '04',
    title: "4LDKは本当に必要？家族構成別の最適な間取りプラン｜平屋・2階建ての比較（鳥取版）",
    category: "物件・プランを決める",
    date: "2026年3月28日",
    readTime: "約17分",
    image: "https://public.readdy.ai/ai/img_res/edited_855076ca2b6086caae04c59a9dd2998a_97537863.jpg",
    excerpt: "「4LDKがいいのか？平屋と2階建て、どっちが暮らしやすい？」鳥取市・米子市・倉吉市の20〜30代の方へ。夫婦2人〜子ども3人以上まで、家族構成別の最適な間取りプランと平屋・2階建て比較を徹底解説します。",
    tags: ["鳥取", "4LDK", "平屋"],
    content: `
      <h2>はじめに</h2>
      <p>間取りを考えるとき、多くの人が最初に悩むのが「4LDKがいいのか？」「平屋と2階建て、どっちが暮らしやすい？」という問題です。鳥取市・米子市・倉吉市の20〜30代の方からも、「子どもが増えたら？」「将来の生活は？」といった不安をよく聞きます。間取りは"家族構成 × ライフスタイル × 将来の変化"で最適解が変わるため、万人にとっての正解はありません。</p>
      <p>ここでは、家族構成別に最適な間取りを整理し、平屋と2階建ての特徴を比較しながら、後悔しない選び方をまとめました。</p>

      <h2>家族構成で変わる「最適な間取り」の考え方</h2>
      <p>間取りは家族の人数だけでなく、生活スタイルや将来の変化によって必要な部屋数が変わります。次の3つの視点で考えると、最適な間取りが見えてきます。</p>
      <ul>
        <li>今の暮らしに必要な部屋数</li>
        <li>5〜10年後の暮らしに必要な部屋数</li>
        <li>老後の暮らしに必要な部屋数</li>
      </ul>

      <h2>① 夫婦2人（DINKS）の場合</h2>
      <p><strong>最適な間取り：2LDK〜3LDK</strong></p>
      <p><strong>理由</strong></p>
      <ul>
        <li>夫婦それぞれのワークスペースが必要なケースが増えている</li>
        <li>来客用の部屋は必須ではない</li>
        <li>将来子どもができた場合は柔軟に対応できる間取りが理想</li>
      </ul>
      <p><strong>おすすめの間取り例</strong></p>
      <ul>
        <li>LDK＋寝室＋ワークスペース</li>
        <li>LDK＋寝室＋将来子ども部屋になるフリールーム</li>
      </ul>

      <h2>② 夫婦＋子ども1人の場合</h2>
      <p><strong>最適な間取り：3LDK〜4LDK</strong></p>
      <p><strong>理由</strong></p>
      <ul>
        <li>子ども部屋は1つでOK</li>
        <li>もう1部屋は「将来の子ども部屋」または「書斎」に使える</li>
        <li>収納量が圧倒的に必要</li>
      </ul>
      <p><strong>おすすめの間取り例</strong></p>
      <ul>
        <li>LDK＋主寝室＋子ども部屋＋フリールーム</li>
        <li>平屋なら3LDK、2階建てなら4LDKがバランス良い</li>
      </ul>

      <h2>③ 夫婦＋子ども2人の場合（最も多いケース）</h2>
      <p><strong>最適な間取り：4LDKが最も安定</strong></p>
      <p><strong>理由</strong></p>
      <ul>
        <li>子ども部屋2つ</li>
        <li>主寝室1つ</li>
        <li>もう1部屋は書斎・収納・来客用に使える</li>
        <li>将来のライフスタイル変化に対応しやすい</li>
      </ul>
      <p><strong>おすすめの間取り例</strong></p>
      <ul>
        <li>1階：LDK＋和室（将来の親の部屋にも）</li>
        <li>2階：主寝室＋子ども部屋2つ</li>
      </ul>
      <p>鳥取では「<strong>1階に1部屋</strong>」が人気で、老後の暮らしにも対応できます。</p>

      <h2>④ 夫婦＋子ども3人以上の場合</h2>
      <p><strong>最適な間取り：4LDK〜5LDK</strong></p>
      <p><strong>理由</strong></p>
      <ul>
        <li>子ども部屋を共有するかどうかで必要部屋数が変わる</li>
        <li>収納量が圧倒的に必要</li>
        <li>2階建てが現実的（平屋は土地が広く必要）</li>
      </ul>
      <p><strong>おすすめの間取り例</strong></p>
      <ul>
        <li>子ども部屋3つ＋主寝室＋フリールーム</li>
        <li>子ども部屋を広めにして将来仕切る</li>
      </ul>

      <h2>平屋と2階建ての比較（鳥取版）</h2>
      <p>鳥取県は土地が比較的広く確保しやすく、平屋も人気があります。それぞれの特徴を比較すると、選びやすくなります。</p>

      <h3>平屋の特徴</h3>
      <ul>
        <li>階段がなく老後も安心</li>
        <li>家事動線が短い</li>
        <li>メンテナンスがしやすい</li>
        <li>建築費は2階建てより高くなりやすい</li>
        <li>広い土地が必要（60坪以上が理想）</li>
      </ul>

      <h3>2階建ての特徴</h3>
      <ul>
        <li>建築費が抑えやすい</li>
        <li>部屋数を確保しやすい</li>
        <li>プライバシーを確保しやすい</li>
        <li>冬の暖房効率は平屋より良い場合も</li>
      </ul>

      <table>
        <thead>
          <tr><th>比較項目</th><th>平屋</th><th>2階建て</th></tr>
        </thead>
        <tbody>
          <tr><td>建築費</td><td>高くなりやすい</td><td>抑えやすい</td></tr>
          <tr><td>動線</td><td>短い・シンプル</td><td>上下移動が必要</td></tr>
          <tr><td>老後の暮らし</td><td>◎ バリアフリー対応しやすい</td><td>△ 将来が心配</td></tr>
          <tr><td>必要な土地</td><td>広め（60坪以上が理想）</td><td>標準（40〜50坪）</td></tr>
          <tr><td>プライバシー</td><td>△ 1階のみ</td><td>◎ 上下で分けやすい</td></tr>
        </tbody>
      </table>

      <p>鳥取市・米子市の市街地では<strong>2階建てが主流</strong>、郊外では<strong>平屋も人気</strong>です。</p>

      <h2>4LDKは本当に必要？判断基準まとめ</h2>
      <p>4LDKが必要かどうかは、次の3つで判断できます。</p>
      <ul>
        <li>子どもの人数（今＋将来）</li>
        <li>書斎・ワークスペースの必要性</li>
        <li>老後の暮らし方（1階で完結したいか）</li>
      </ul>
      <p>特に鳥取では、<strong>室内干しスペース・ファミリークローゼット・1階に1部屋</strong>があると暮らしやすさが大きく向上します。</p>

      <h2>まとめ</h2>
      <p>家族構成別の最適な間取りは次の通りです。</p>
      <table>
        <thead>
          <tr><th>家族構成</th><th>最適な間取り</th></tr>
        </thead>
        <tbody>
          <tr><td>夫婦2人</td><td>2LDK〜3LDK</td></tr>
          <tr><td>子ども1人</td><td>3LDK〜4LDK</td></tr>
          <tr><td>子ども2人</td><td>4LDKが安定</td></tr>
          <tr><td>子ども3人以上</td><td>4LDK〜5LDK</td></tr>
        </tbody>
      </table>
      <p>平屋は動線が良く、2階建ては部屋数を確保しやすいという特徴があります。<strong>家族の今と未来をイメージしながら</strong>、最適な間取りを選ぶことが大切です。</p>

      <h2>次に読む記事</h2>
      <p>家族構成別の間取りが分かったら、次に気になるのは「住宅会社との間取り打ち合わせをどう進めれば失敗しないか？」という点です。</p>
      <p>👉 <a href="/articles/52">【保存版】住宅会社とつくる間取り打ち合わせの進め方｜準備・チェックリスト・NG例（鳥取版）</a>を読むと、打ち合わせがスムーズになり、後悔のない家づくりにつながります。</p>
    `
  },
  {
    id: 52,
    step: '04',
    title: "【保存版】住宅会社とつくる間取り打ち合わせの進め方｜準備・チェックリスト・NG例（鳥取版）",
    category: "物件・プランを決める",
    date: "2026年3月28日",
    readTime: "約18分",
    image: "https://public.readdy.ai/ai/img_res/edited_0f7ef8ec196f79755df467562abf0d82_ce36c3e6.jpg",
    excerpt: "「打ち合わせで何を準備すればいいの？」「どう伝えれば理想の間取りになる？」鳥取市・米子市・倉吉市の20〜30代の方へ。間取り打ち合わせの流れ・準備・チェックポイント・NG例まで体系的にまとめた保存版ガイドです。",
    tags: ["間取り打ち合わせ", "チェックリスト", "NG例"],
    content: `
      <h2>はじめに</h2>
      <p>間取りの基本と家族構成別の最適プランが整理できたら、次に待っているのが住宅会社との「間取り打ち合わせ」です。鳥取市・米子市・倉吉市の20〜30代の方からは、「打ち合わせで何を準備すればいいの？」「どう伝えれば理想の間取りになる？」という相談がとても多く、ここでつまずくと後悔につながりやすい工程でもあります。</p>
      <p><strong>間取り打ち合わせは、準備の質で結果が決まる</strong>と言っても過言ではありません。この記事では、初心者でも迷わず進められるように、打ち合わせの流れ・準備・チェックポイント・NG例まで体系的にまとめました。</p>

      <h2>打ち合わせ前に必ずやるべき準備</h2>
      <p>間取り打ち合わせは、<strong>事前準備が8割</strong>です。準備ができていると、打ち合わせがスムーズになり、理想の間取りに近づきます。</p>

      <h3>1. 家族の生活スタイルを整理する</h3>
      <ul>
        <li>起床・就寝時間</li>
        <li>洗濯の頻度</li>
        <li>料理のスタイル（自炊中心か）</li>
        <li>子どもの年齢・将来の部屋の使い方</li>
        <li>在宅ワークの有無</li>
      </ul>
      <p>生活スタイルを共有すると、住宅会社が"暮らしに合う間取り"を提案しやすくなります。</p>

      <h3>2. 必要な部屋数を決める</h3>
      <ul>
        <li>主寝室</li>
        <li>子ども部屋（今＋将来）</li>
        <li>書斎・ワークスペース</li>
        <li>和室の有無</li>
        <li>収納の量</li>
      </ul>
      <p>「絶対必要」「できれば」「なくてもOK」の3段階で整理すると明確になります。</p>

      <h3>3. 好きな間取り・嫌いな間取りを集める</h3>
      <p>SNSや住宅会社の施工事例から、好きな間取り・嫌いな間取りを10個ずつ集めると、打ち合わせが一気に進みます。</p>
      <p>※Pinterestというアプリで気になる間取りや収納アイデアを営業担当者と共有しながら打ち合わせを進めると、<strong>イメージの食い違いがなくてスムーズに進む</strong>のでおすすめです！</p>

      <h2>打ち合わせの流れ（初回〜最終）</h2>

      <h3>初回：ヒアリング・要望整理</h3>
      <ul>
        <li>家族構成</li>
        <li>生活スタイル</li>
        <li>予算</li>
        <li>土地の条件</li>
        <li>好きな間取りの傾向</li>
      </ul>
      <p>ここで<strong>要望を出し切ることが重要</strong>です。</p>

      <h3>2回目：ラフプランの提案</h3>
      <p>住宅会社が最初の間取り案を作成します。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>動線はスムーズか</li>
        <li>収納は足りているか</li>
        <li>日当たりは確保できているか</li>
        <li>生活のイメージが湧くか</li>
      </ul>

      <h3>3回目：修正プランの確認</h3>
      <p>細かい修正を行います。</p>
      <p><strong>例</strong></p>
      <ul>
        <li>収納の位置変更</li>
        <li>コンセントの追加</li>
        <li>窓の位置調整</li>
        <li>部屋の広さ調整</li>
      </ul>

      <h3>最終：間取り確定</h3>
      <p>ここで間取りが確定し、見積もりが確定します。</p>

      <h2>間取り打ち合わせで必ず見るべきポイント</h2>

      <h3>1. 家事動線</h3>
      <ul>
        <li>洗濯 → 干す → しまう</li>
        <li>料理 → 配膳 → 片付け</li>
        <li>ゴミ出し動線</li>
      </ul>
      <p>鳥取は冬の天候が不安定なため、<strong>室内干し動線は特に重要</strong>です。</p>

      <h3>2. 収納の位置</h3>
      <p>収納は「量」より「位置」が大事です。</p>
      <p><strong>チェックポイント</strong></p>
      <ul>
        <li>玄関収納</li>
        <li>パントリー</li>
        <li>ファミリークローゼット</li>
        <li>リビング収納</li>
      </ul>

      <h3>3. 日当たり</h3>
      <ul>
        <li>南向きリビング</li>
        <li>朝日が入る寝室</li>
        <li>西日の対策</li>
      </ul>

      <h3>4. 家具配置</h3>
      <p>家具が置けない間取りは失敗しやすいです。ソファ・ベッド・ダイニングテーブルを実際に配置して確認しましょう。</p>

      <h2>間取り打ち合わせでやってはいけないNG例</h2>

      <h3>NG①：要望を後出しする</h3>
      <p>後から出した要望は、間取りが崩れたり追加費用が発生しやすいです。</p>

      <h3>NG②：SNSの間取りをそのまま真似する</h3>
      <p>土地・家族構成が違うため、必ずしも合うとは限りません。</p>

      <h3>NG③：動線よりデザインを優先する</h3>
      <p>デザイン優先の間取りは、住んでから不便になりがちです。</p>

      <h3>NG④：収納を"量"だけで考える</h3>
      <p>使う場所の近くに収納がないと、片付かない家になります。</p>

      <h2>打ち合わせを成功させるコツ</h2>
      <ul>
        <li>要望は最初にすべて伝える</li>
        <li>生活スタイルを細かく共有する</li>
        <li>家族全員の意見をまとめておく</li>
        <li>修正は「理由」を伝える</li>
        <li>迷ったらプロの意見を聞く</li>
      </ul>
      <p>間取りは<strong>"プロと一緒につくる"</strong>意識が大切です。</p>

      <h2>まとめ</h2>
      <p>間取り打ち合わせは、</p>
      <ul>
        <li>生活スタイルの整理</li>
        <li>必要な部屋数の明確化</li>
        <li>好きな間取りの共有</li>
        <li>動線・収納・日当たりのチェック</li>
        <li>NG例を避ける</li>
      </ul>
      <p>これらを押さえることで、<strong>後悔のない家づくりにつながります</strong>。</p>

      <h2>次に読む記事</h2>
      <p>間取り打ち合わせの進め方が分かったら、次に気になるのは「土地＋間取りの総額をどう抑えるか？」という点です。家づくりの総額をコントロールできると、間取りの自由度も大きく広がります。</p>
      <p>👉 <a href="/articles/53">土地＋建物の総額を抑える方法｜予算内で理想の家を建てるための現実的なテクニック（鳥取版）</a></p>
    `
  },
  {
    id: 53,
    step: '04',
    title: "土地＋建物の総額を抑える方法｜予算内で理想の家を建てるための現実的なテクニック（鳥取版）",
    category: "物件・プランを決める",
    date: "2026年3月28日",
    readTime: "約18分",
    image: "https://public.readdy.ai/ai/img_res/edited_ffe33a77ce969f8c18918e79a315207e_ac25b92c.jpg",
    excerpt: "「総額が思ったより高くなる」「気づいたら予算オーバーしていた」鳥取市・米子市・倉吉市の20〜30代の方へ。土地・建物・外構・諸費用の4つを最適化する現実的な節約テクニックを徹底解説します。",
    tags: ["総額", "節約テクニック", "予算"],
    content: `
      <h2>はじめに</h2>
      <p>家づくりで最も多い悩みが「総額が思ったより高くなる」という問題です。鳥取市・米子市・倉吉市の20〜30代の方からも、「土地と建物のバランスが難しい」「気づいたら予算オーバーしていた」という声をよく聞きます。</p>
      <p>総額を抑えるためには、<strong>土地・建物・外構・諸費用の"4つの費用"をどう最適化するか</strong>が鍵になります。この記事では、初心者でも実践できる"現実的で効果の高い節約テクニック"をまとめました。</p>

      <h2>総額を抑えるための基本構造</h2>
      <p>家づくりの総額は次の4つで決まります。</p>
      <ul>
        <li>土地代</li>
        <li>建物本体価格</li>
        <li>外構費</li>
        <li>諸費用（登記・ローン・火災保険など）</li>
      </ul>
      <p>この4つのどこを調整するかで、総額は大きく変わります。</p>

      <h2>① 土地代を抑えるテクニック（効果が大きい）</h2>
      <p>土地は総額の中で最も調整しやすい部分です。</p>

      <h3>1. 土地の広さを"必要最低限"にする</h3>
      <p>鳥取は土地が広い傾向がありますが、広すぎると外構費が高くなります。</p>
      <ul>
        <li>4人家族なら<strong>55〜65坪が最もコスパが良い</strong></li>
        <li>70坪以上は外構費が跳ね上がりやすい</li>
      </ul>

      <h3>2. 角地より"中地"を選ぶ</h3>
      <p>角地は人気ですが、価格が高く外構費も増えます。</p>
      <ul>
        <li>中地の方が<strong>土地代が安い＋外構費も抑えられる</strong></li>
      </ul>

      <h3>3. 整形地にこだわりすぎない</h3>
      <p>多少の変形地でも、間取り次第で十分暮らしやすくできます。</p>
      <ul>
        <li>整形地より<strong>50〜150万円安い</strong>ケースが多い</li>
      </ul>

      <h3>4. インフラ状況を確認する</h3>
      <ul>
        <li>上水道引き込みなし → 追加費用20〜50万円</li>
        <li>下水道なし → 浄化槽で＋50〜80万円</li>
      </ul>
      <p>土地代が安くても、インフラで高くなるケースは多いです。</p>

      <h2>② 建物本体価格を抑えるテクニック（無理なく節約）</h2>
      <p>建物価格は"削り方を間違えると後悔する"部分なので、優先順位が重要です。</p>

      <h3>1. 坪数を1〜2坪減らす（最も効果が大きい）</h3>
      <p>坪単価60〜80万円の場合、<strong>1坪減らすだけで60〜80万円の節約</strong>になります。</p>
      <ul>
        <li>収納の位置を工夫すれば、広さを感じやすい</li>
        <li>動線を整えると、無駄な廊下を減らせる</li>
      </ul>

      <h3>2. 形をシンプルにする（凹凸を減らす）</h3>
      <p>凹凸が多い家はコストが上がります。</p>
      <ul>
        <li>正方形・長方形の家は<strong>コスパ最強</strong></li>
        <li>屋根の形もシンプルにするとさらに節約</li>
      </ul>

      <h3>3. 水回りをまとめる</h3>
      <p>キッチン・洗面・お風呂を近くに配置すると配管が短くなり、<strong>20〜40万円の節約</strong>につながります。</p>

      <h3>4. 造作を減らし、既製品を活用</h3>
      <ul>
        <li>造作棚 → 可動棚</li>
        <li>造作洗面 → 既製品＋ミラーキャビネット</li>
      </ul>
      <p>造作は魅力的ですが、コストが跳ね上がりやすい部分です。</p>

      <h2>③ 外構費を抑えるテクニック（見落としがちな節約ポイント）</h2>
      <p>外構費は<strong>80〜150万円</strong>が相場ですが、工夫次第で抑えられます。</p>

      <h3>1. 駐車場コンクリートを最小限にする</h3>
      <p>全面コンクリートは高額です。必要な部分だけコンクリートにし、残りは砂利にするだけで<strong>20〜40万円節約</strong>できます。</p>

      <h3>2. フェンスを必要な部分だけにする</h3>
      <p>全面フェンスは高額です。</p>
      <ul>
        <li>隣家との境界だけ</li>
        <li>道路側は植栽で代用</li>
      </ul>

      <h3>3. ウッドデッキは後付けでもOK</h3>
      <p>ウッドデッキは10〜30万円します。引き渡し後にDIYや外部業者で依頼すると安くなります。</p>

      <h2>④ 諸費用を抑えるテクニック（知らないと損する部分）</h2>
      <p>諸費用は<strong>150〜250万円</strong>が一般的ですが、工夫次第で抑えられます。</p>

      <h3>1. 火災保険は補償内容を最適化</h3>
      <p>鳥取は雪災・風災リスクがあるため、補償は必要ですが、<strong>不要な特約を外すだけで数万円節約</strong>できます。</p>

      <h3>2. 住宅ローンの比較をする</h3>
      <p>金利差は総額に大きく影響します。</p>
      <ul>
        <li>地銀（鳥取銀行・山陰合同銀行）</li>
        <li>ネット銀行（auじぶん銀行・ソニー銀行など）</li>
      </ul>
      <p><strong>0.1％の差で数十万円の差</strong>になります。</p>

      <h3>3. 引っ越し費用は早めに予約</h3>
      <p>3月は繁忙期で高額になるため、早めの予約が節約につながります。</p>

      <h2>総額を抑えるための"優先順位"の考え方</h2>
      <p>節約の優先順位は次の通りです。</p>
      <ol>
        <li>土地の広さ・形状・インフラ</li>
        <li>建物の坪数・形・水回り配置</li>
        <li>外構のコンクリート・フェンス・植栽</li>
        <li>諸費用を最適化する</li>
      </ol>
      <p>この順番で調整すると、無理なく予算内に収まりやすくなります。</p>

      <h2>まとめ</h2>
      <p>土地＋建物の総額を抑えるためには、次の4つを最適化することが重要です。</p>
      <ul>
        <li>土地の広さ・形状・インフラ</li>
        <li>建物の坪数・形・水回り配置</li>
        <li>外構のコンクリート・フェンス・植栽</li>
        <li>諸費用（火災保険・ローン・引っ越し）</li>
      </ul>
      <p>"削るところ"と"削ってはいけないところ"を見極めることで、<strong>予算内で理想の家を実現できます</strong>。</p>

      <h2>次に読む記事</h2>
      <p>間取りの考え方や打ち合わせの進め方が分かると、次に気になるのは「実際にどんな間取りが成功しているのか？」という"リアルな事例"です。特に鳥取では、冬の日当たり・室内干しスペース・回遊動線・1階完結型の生活など、地域特有のニーズがあります。</p>
      <p>👉 <a href="/articles/40">鳥取で後悔しない間取り10選｜4LDK・平屋・回遊動線の成功例まとめ</a>を読むと、あなたの家族に合う間取りのイメージが一気に具体化します。</p>
    `
  }
];
